import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Task, Category } from '../../types';
import { Button } from '../base/Button';
import { Input } from '../base/Input';
import { Select } from '../base/Select';

interface TaskFormProps {
  task?: Task;
  categories: Category[];
  tasks: Task[];
  onSubmit: (task: Omit<Task, 'id'>) => void;
  onCancel: () => void;
}

export const TaskForm = ({ task, categories, tasks, onSubmit, onCancel }: TaskFormProps) => {
  const [formData, setFormData] = useState({
    name: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    categoryId: categories[0]?.id || '',
    parentId: '',
    dependencies: [] as string[],
    progress: 0,
    description: '',
  });

  useEffect(() => {
    if (task) {
      setFormData({
        name: task.name,
        startDate: format(task.startDate, 'yyyy-MM-dd'),
        endDate: format(task.endDate, 'yyyy-MM-dd'),
        categoryId: task.categoryId,
        parentId: task.parentId || '',
        dependencies: task.dependencies,
        progress: task.progress,
        description: task.description || '',
      });
    }
  }, [task]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    onSubmit({
      name: formData.name,
      startDate: new Date(formData.startDate),
      endDate: new Date(formData.endDate),
      categoryId: formData.categoryId,
      parentId: formData.parentId || undefined,
      dependencies: formData.dependencies,
      progress: formData.progress,
      description: formData.description,
    });
  };

  const availableParentTasks = tasks.filter(t => 
    t.id !== task?.id && !t.parentId && t.id !== formData.parentId
  );

  const availableDependencies = tasks.filter(t => 
    t.id !== task?.id && !formData.dependencies.includes(t.id)
  );

  const toggleDependency = (taskId: string) => {
    setFormData(prev => ({
      ...prev,
      dependencies: prev.dependencies.includes(taskId)
        ? prev.dependencies.filter(id => id !== taskId)
        : [...prev.dependencies, taskId]
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Task Name"
        value={formData.name}
        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
        required
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Start Date"
          type="date"
          value={formData.startDate}
          onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
          required
        />
        <Input
          label="End Date"
          type="date"
          value={formData.endDate}
          onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
          required
        />
      </div>

      <Select
        label="Category"
        options={categories.map(cat => ({ value: cat.id, label: cat.name }))}
        value={formData.categoryId}
        onChange={(value) => setFormData(prev => ({ ...prev, categoryId: value }))}
      />

      <Select
        label="Parent Task (Optional)"
        options={[
          { value: '', label: 'No parent task' },
          ...availableParentTasks.map(task => ({ value: task.id, label: task.name }))
        ]}
        value={formData.parentId}
        onChange={(value) => setFormData(prev => ({ ...prev, parentId: value }))}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Progress ({formData.progress}%)
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={formData.progress}
          onChange={(e) => setFormData(prev => ({ ...prev, progress: parseInt(e.target.value) }))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Dependencies
        </label>
        <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
          {availableDependencies.length === 0 ? (
            <p className="text-sm text-gray-500">No available dependencies</p>
          ) : (
            availableDependencies.map(depTask => (
              <label key={depTask.id} className="flex items-center gap-2 p-1 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.dependencies.includes(depTask.id)}
                  onChange={() => toggleDependency(depTask.id)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">{depTask.name}</span>
              </label>
            ))
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Task description..."
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="submit" variant="primary">
          {task ? 'Update Task' : 'Create Task'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};